import { DateTime } from 'luxon';

import { FilterComparisonOperator, FilterGroup, FilterLogicalOperator } from '@/common/models';
import DateFilterComparisonOptions from '@/common/models/query-params/DateFilterComparisonOptions';
import Filter from '@/common/models/query-params/Filter';
import FilterBase from '@/common/models/query-params/FilterBase';
import { toServerDate, toServerDateTime } from '@/globals';
import i18n from '@/vueI18n';

interface IDateFilterAPI {
  type: string;
  property: string;
  label: string;
  placeholder: string;
  comparison_option: string;
  first_date: DateTime;
  second_date: DateTime;
  before: DateTime;
  after: DateTime;
  duration_value: number;
  duration_unit: string;
  range_start: string;
  range_end: string;
  instance?: string;
}

export default class DateFilter extends FilterBase {
  type: string;
  property: string;
  label: string;
  placeholder: string;
  comparisonOption: string;
  firstDate: DateTime;
  secondDate: DateTime;
  before: DateTime;
  after: DateTime;
  durationValue: number;
  durationUnit: string;
  rangeStart: string;
  rangeEnd: string;
  instance: string;

  constructor(
    property = '',
    label = null,
    comparisonOption = null,
    firstDate = null,
    secondDate = null,
    before = null,
    after = null,
    durationValue = null,
    durationUnit = 'day',
    rangeStart = null,
    rangeEnd = null,
  ) {
    super();
    this.type = 'DateFilter';
    this.property = property;
    this.label = label;
    this.placeholder = null;
    this.comparisonOption = comparisonOption;
    this.firstDate = firstDate;
    this.secondDate = secondDate;
    this.before = before;
    this.after = after;
    this.durationValue = durationValue;
    this.durationUnit = durationUnit;
    this.rangeStart = rangeStart;
    this.rangeEnd = rangeEnd;
  }

  get isEmpty() {
    return this.comparisonOption == null;
  }

  static fromObject(obj) {
    return super.fromObject(obj, new DateFilter());
  }

  get duration() {
    const duration = {};
    duration[this.durationUnit] = this.durationValue;
    return duration;
  }

  get rangeStartDate() {
    return DateFilter.getRangeDate(this.rangeStart);
  }

  get rangeEndDate() {
    return DateFilter.getRangeDate(this.rangeEnd);
  }

  get description() {
    switch (this.comparisonOption) {
      case DateFilterComparisonOptions.WithinTheLast.id:
        return i18n.global.t('messages.withinTheLastTimePeriod', [
          this.durationValue,
          i18n.global.t(`names.${this.durationUnit}`, this.durationValue),
        ]);
      case DateFilterComparisonOptions.MoreThan.id:
        return i18n.global.t('messages.moreThanTimePeriodAgo', [
          this.durationValue,
          i18n.global.t(`names.${this.durationUnit}`, this.durationValue),
        ]);
      case DateFilterComparisonOptions.Between.id:
        return i18n.global.t('messages.betweenStartDateAndEndDate', [
          this.firstDate.toLocaleString(DateTime.DATE_SHORT),
          this.secondDate.toLocaleString(DateTime.DATE_SHORT),
        ]);
      case DateFilterComparisonOptions.Before.id:
        return i18n.global.t('messages.beforeDate', [this.before.toLocaleString(DateTime.DATE_SHORT)]);
      case DateFilterComparisonOptions.After.id:
        return i18n.global.t('messages.afterDate', [this.after.toLocaleString(DateTime.DATE_SHORT)]);
      case DateFilterComparisonOptions.InRange.id:
        return i18n.global.t('messages.inRangeBetween2Values', [this.rangeStart, this.rangeEnd]);
      default:
        return null;
    }
  }

  static getRangeDate(range) {
    const minus = range.trim().startsWith('-');
    const groups = range.match(/(-?\d{1,}[a-z]{1,})/gm);
    let date = DateTime.local();
    groups.forEach((group) => {
      const [unit] = group.match(/[a-z]{1,}/g);
      const [number] = group.match(/\d{1,}/g);
      if (minus) {
        switch (unit) {
          case 'w':
            date = date.minus({ weeks: number });
            break;
          case 'd':
            date = date.minus({ days: number });
            break;
          case 'h':
            date = date.minus({ hours: number });
            break;
          case 'm':
            date = date.minus({ minutes: number });
            break;
          case 's':
            date = date.minus({ seconds: number });
            break;
          default:
            break;
        }
      } else {
        switch (unit) {
          case 'w':
            date = date.plus({ weeks: number });
            break;
          case 'd':
            date = date.plus({ days: number });
            break;
          case 'h':
            date = date.plus({ hours: number });
            break;
          case 'm':
            date = date.plus({ minutes: number });
            break;
          case 's':
            date = date.plus({ seconds: number });
            break;
          default:
            break;
        }
      }
    });
    return date;
  }

  getFilters() {
    switch (this.comparisonOption) {
      case DateFilterComparisonOptions.WithinTheLast.id:
        return [
          new FilterGroup(
            [
              new Filter(
                this.property,
                [toServerDateTime(DateTime.local().minus(this.duration))],
                FilterComparisonOperator.GREATER_THAN_OR_EQUAL,
              ),
              new Filter(
                this.property,
                [toServerDateTime(DateTime.local())],
                FilterComparisonOperator.LESS_THAN_OR_EQUAL,
              ),
            ],
            FilterLogicalOperator.AND,
          ),
        ];
      // return [new Filter(this.property, [toServerDate(DateTime.local().minus(this.duration))], FilterComparisonOperator.GREATER_THAN_OR_EQUAL)];
      case DateFilterComparisonOptions.MoreThan.id:
        return [
          new Filter(
            this.property,
            [DateTime.local().minus(this.duration)],
            FilterComparisonOperator.LESS_THAN_OR_EQUAL,
          ),
        ];
      case DateFilterComparisonOptions.Between.id:
        return [
          new FilterGroup(
            [
              new Filter(this.property, [toServerDate(this.firstDate)], FilterComparisonOperator.GREATER_THAN_OR_EQUAL),
              new Filter(this.property, [toServerDate(this.secondDate)], FilterComparisonOperator.LESS_THAN_OR_EQUAL),
            ],
            FilterLogicalOperator.AND,
          ),
        ];
      case DateFilterComparisonOptions.Before.id:
        return [new Filter(this.property, [toServerDate(this.before)], FilterComparisonOperator.LESS_THAN)];
      case DateFilterComparisonOptions.After.id:
        return [new Filter(this.property, [toServerDate(this.after)], FilterComparisonOperator.GREATER_THAN)];
      case DateFilterComparisonOptions.InRange.id:
        return [
          new FilterGroup(
            [
              new Filter(
                this.property,
                [toServerDate(this.rangeStartDate)],
                FilterComparisonOperator.GREATER_THAN_OR_EQUAL,
              ),
              new Filter(this.property, [toServerDate(this.rangeEndDate)], FilterComparisonOperator.LESS_THAN_OR_EQUAL),
            ],
            FilterLogicalOperator.AND,
          ),
        ];
      default:
        return [];
    }
  }

  clear() {
    this.comparisonOption = null;
    this.firstDate = null;
    this.secondDate = null;
    this.before = null;
    this.after = null;
    this.durationValue = null;
    this.durationUnit = null;
    this.rangeStart = null;
    this.rangeEnd = null;
  }

  serializeForQueryString() {
    switch (this.comparisonOption) {
      case DateFilterComparisonOptions.WithinTheLast.id:
      case DateFilterComparisonOptions.MoreThan.id:
        return `${this.property}~${this.comparisonOption}~${this.durationValue}~${this.durationUnit}`;
      case DateFilterComparisonOptions.Between.id:
        return `${this.property}~${this.comparisonOption}~${this.firstDate.toMillis()}~${this.secondDate.toMillis()}`;
      case DateFilterComparisonOptions.Before.id:
        return `${this.property}~${this.comparisonOption}~${this.before.toMillis()}`;
      case DateFilterComparisonOptions.After.id:
        return `${this.property}~${this.comparisonOption}~${this.after.toMillis()}`;
      case DateFilterComparisonOptions.InRange.id:
        return `${this.property}~${this.comparisonOption}~${this.rangeStart
          .replace('-', 'minus')
          .replace('+', 'plus')}~${this.rangeEnd.replace('-', 'minus').replace('+', 'plus')}`;
      default:
        return `${this.property}~`;
    }
  }

  static deserializeFromQueryString(filter, serialized) {
    const fragments = serialized.split('~');
    if (fragments[1] === '') {
      return new DateFilter(filter.property, filter.label);
    }
    const comparisonOption = DateFilterComparisonOptions.all.find((x) => x.id === fragments[1]).id;
    const firstDate =
      comparisonOption === DateFilterComparisonOptions.Between.id
        ? DateTime.fromMillis(Number.parseInt(fragments[2], 10))
        : null;
    const secondDate =
      comparisonOption === DateFilterComparisonOptions.Between.id
        ? DateTime.fromMillis(Number.parseInt(fragments[3], 10))
        : null;
    const before =
      comparisonOption === DateFilterComparisonOptions.Before.id
        ? DateTime.fromMillis(Number.parseInt(fragments[2], 10))
        : null;
    const after =
      comparisonOption === DateFilterComparisonOptions.After.id
        ? DateTime.fromMillis(Number.parseInt(fragments[2], 10))
        : null;
    const durationValue =
      comparisonOption === DateFilterComparisonOptions.WithinTheLast.id ||
      comparisonOption === DateFilterComparisonOptions.MoreThan.id
        ? fragments[2]
        : null;
    const durationUnit =
      comparisonOption === DateFilterComparisonOptions.WithinTheLast.id ||
      comparisonOption === DateFilterComparisonOptions.MoreThan.id
        ? fragments[3]
        : null;
    const rangeStart =
      comparisonOption === DateFilterComparisonOptions.InRange.id
        ? fragments[2].replace('minus', '-').replace('plus', '+')
        : null;
    const rangeEnd =
      comparisonOption === DateFilterComparisonOptions.InRange.id
        ? fragments[3].replace('minus', '-').replace('plus', '+')
        : null;
    return new DateFilter(
      filter.property,
      filter.label,
      comparisonOption,
      firstDate,
      secondDate,
      before,
      after,
      durationValue,
      durationUnit,
      rangeStart,
      rangeEnd,
    );
  }

  toApi(): IDateFilterAPI {
    return {
      type: this.type,
      property: this.property,
      label: this.label,
      placeholder: this.placeholder,
      comparison_option: this.comparisonOption,
      first_date: this.firstDate,
      second_date: this.secondDate,
      before: this.before,
      after: this.after,
      duration_value: this.durationValue,
      duration_unit: this.durationUnit,
      range_start: this.rangeStart,
      range_end: this.rangeEnd,
    };
  }

  static fromApi(apiObject: IDateFilterAPI): DateFilter {
    const filter = new DateFilter(
      apiObject.property,
      apiObject.label,
      apiObject.comparison_option,
      apiObject.first_date,
      apiObject.second_date,
      apiObject.before,
      apiObject.after,
      apiObject.duration_value,
      apiObject.duration_unit,
      apiObject.range_start,
      apiObject.range_end,
    );
    filter.instance = apiObject.instance;
    return filter;
  }
}
